import React from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CandidateService } from "@/services/candidateService";
import { Textarea } from "@/components/ui/Textarea";
import { ButtonShadcn } from "@/components/ui/Button";
import { reviewCategories } from "../../constants/reviewCategories";
import { Card } from "@/components/ui/Card";
import { Send } from "lucide-react";
import { feedbackSchema } from "./feedbackSchema";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { fetchCandidateAnalysis } from "@/redux/reducers/CandidateReducer";

const EditableFeedbackForm = ({ candidateId, applicationId, onSuccess, jobId }) => {
  const dispatch = useDispatch();

  const defaultValues = {
    feedback: reviewCategories.reduce((acc, cat) => {
      acc[cat.id] = { rating: 0, review: "" };
      return acc;
    }, {}),
    additionalNotes: "",
  };

  const methods = useForm({
    resolver: zodResolver(feedbackSchema),
    mode: "onChange",
    defaultValues,
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const feedback = useWatch({ control, name: "feedback" });
  const additionalNotes = useWatch({ control, name: "additionalNotes" });

  const handleRatingChange = (categoryId, rating) => {
    const currentRating = feedback?.[categoryId]?.rating || 0;
    setValue(`feedback.${categoryId}.rating`, currentRating === rating ? 0 : rating, { shouldValidate: true });
  };

  const handleReviewChange = (categoryId, review) => {
    setValue(`feedback.${categoryId}.review`, review, { shouldValidate: true });
  };

  const onFormSubmit = async (data) => {
    const feedback_categories = {};
    const category_reviews = {};

    reviewCategories.forEach((cat) => {
      const item = data.feedback[cat.id];
      feedback_categories[cat.key] = item.rating;
      category_reviews[cat.key] = item.review.trim();
    });

    const totalScore = Object.values(feedback_categories).reduce((sum, r) => sum + r, 0);
    const normalizedScore = Number(((totalScore / 30) * 10).toFixed(1));

    const payload = {
      feedback_score: normalizedScore,
      feedback_notes: data.additionalNotes.trim(),
      feedback_categories,
      category_reviews,
    };

    try {
      const response = await CandidateService.submitCandidateFeedback(candidateId, applicationId, payload);
      onSuccess?.(response?.data || payload);
      toast.success("Feedback submitted successfully");
      dispatch(fetchCandidateAnalysis({ candidateId, jobId }));
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit feedback");
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="space-y-3 mb-6">
        {reviewCategories.map((category) => {
          const current = feedback?.[category.id] || { rating: 0, review: "" };
          const Icon = category.icon;

          return (
            <Card key={category.id} className="p-4 rounded-xl bg-background-secondary border border-border-primary shadow-sm">
              <div className="space-y-3">

                {/* Header */}
                <div className="flex items-start gap-3">
                  <div className="p-1.5 w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center border border-blue-100 dark:border-blue-500/20 flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-text-primary text-sm font-semibold leading-snug">{category.title}</h3>
                    <p className="text-text-secondary text-xs mt-0.5">{category.description}</p>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <ButtonShadcn
                      variant="ghost"
                      type="button"
                      key={num}
                      onClick={() => handleRatingChange(category.id, num)}
                      className={`w-8 h-8 rounded-full border text-xs font-medium transition-all
                        ${current.rating === num
                          ? "bg-blue-500 text-text-base border-blue-500 shadow-sm hover:bg-blue-500"
                          : "border-border-primary bg-background-primary text-text-muted hover:border-blue-300 hover:text-blue-500"
                        }`}
                    >
                      {num}
                    </ButtonShadcn>
                  ))}
                </div>

                {errors?.feedback?.[category.id]?.rating && (
                  <p className="text-red-500 text-xs">{errors.feedback[category.id].rating.message}</p>
                )}

                {/* Review */}
                <div>
                  <h2 className="mb-1.5 text-xs font-medium text-text-primary">
                    Written Review <span className="text-red-400">*</span>
                  </h2>
                  <Textarea
                    className="min-h-[80px] text-xs bg-background-primary text-text-primary placeholder:text-xs border border-border-primary focus:ring-0 resize-none"
                    placeholder={category.placeholder}
                    value={current.review}
                    onChange={(e) => handleReviewChange(category.id, e.target.value)}
                  />
                </div>

                {errors?.feedback?.[category.id]?.review && (
                  <p className="text-red-500 text-xs">{errors.feedback[category.id].review.message}</p>
                )}
              </div>
            </Card>
          );
        })}

        {/* Additional Notes */}
        <Card className="p-4 rounded-xl bg-background-secondary border border-border-primary shadow-sm">
          <h2 className="mb-1.5 text-xs font-medium text-text-primary">Additional Notes</h2>
          <Textarea
            className="min-h-[80px] text-xs bg-background-primary placeholder:text-xs text-text-primary border border-border-primary focus:ring-0 resize-none"
            placeholder="Overall impression, standout qualities..."
            value={additionalNotes}
            onChange={(e) => setValue("additionalNotes", e.target.value, { shouldValidate: true })}
          />
          {errors?.additionalNotes && (
            <p className="text-red-500 text-xs mt-1.5">{errors.additionalNotes.message}</p>
          )}
        </Card>

        {/* Submit */}
        <ButtonShadcn
          variant="ghost"
          type="button"
          onClick={handleSubmit(onFormSubmit)}
          disabled={isSubmitting}
          className="h-auto flex items-center gap-2 bg-button-secondary text-text-base px-5 py-2 rounded-full text-xs font-medium disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Submitting..." : (
            <>
              <Send className="w-3 h-3" />
              Submit Feedback
            </>
          )}
        </ButtonShadcn>
      </div>
    </FormProvider>
  );
};

export default EditableFeedbackForm;